import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { fromUTCToSaoPaulo } from '../utils/dateUtils';
import AppIcon from './icons/AppIcon';

export default function CRMTimelineModal({ date, events, onClose, onEventClick, onNewEvent }) {
  const hours = Array.from({ length: 13 }, (_, i) => i + 8);
  const [contactsData, setContactsData] = useState({});

  useEffect(() => {
    loadContactsData();
  }, [events]);

  const loadContactsData = async () => {
    const contactIds = events
      .map(e => e.contact_id)
      .filter(id => id);

    if (contactIds.length === 0) return;

    const { data } = await supabase
      .from('contatos')
      .select('id, nome, email, numero')
      .in('id', [...new Set(contactIds)]);

    if (data) {
      const contactsMap = {};
      data.forEach(c => { contactsMap[c.id] = c; });
      setContactsData(contactsMap);
    }
  };

  const getEventStyle = (event) => {
    const start = fromUTCToSaoPaulo(event.start_date);
    const end = event.end_date ? fromUTCToSaoPaulo(event.end_date) : new Date(start.getTime() + 30 * 60 * 1000);
    
    const startHour = start.getHours() + start.getMinutes() / 60;
    const endHour = end.getHours() + end.getMinutes() / 60;
    
    const topPosition = startHour * 80;
    const duration = (endHour - startHour) * 60;
    const height = Math.max(duration, 40);
    
    return {
      top: topPosition + 'px',
      height: height + 'px'
    };
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      meeting: 'groups',
      crm_appointment: 'groups',
      task: 'task',
      deadline: 'flag',
      reminder: 'alarm',
      call: 'phone'
    };
    return icons[type] || 'event';
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 12) {
      return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
    }
    return phone;
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '2rem'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'rgba(15, 12, 41, 0.98)',
          border: '1px solid rgba(180, 59, 234, 0.3)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '1000px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem 2rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'var(--primary-color)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <AppIcon name="calendar_today" style={{ fontSize: '2rem', color: '#44e1ff' }} />
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.5rem' }}>
              {date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </h2>
          </div>
          <button 
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            <AppIcon name="close" />
          </button>
        </div>

        {/* Timeline */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: '1rem',
          background: 'rgba(15, 12, 41, 0.6)',
          minHeight: '500px'
        }}>
          <div style={{ display: 'flex', gap: '1rem', position: 'relative', minHeight: (hours.length * 80) + 'px' }}>
            {/* Hours Column */}
            <div style={{ width: '80px', flexShrink: 0 }}>
              {hours.map(hour => (
                <div 
                  key={hour} 
                  style={{
                    height: '80px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    paddingRight: '1rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <span style={{ color: '#9bb3c7', fontSize: '0.85rem', fontWeight: 500 }}>
                    {hour.toString().padStart(2, '0')}:00
                  </span>
                </div>
              ))}
            </div>

            {/* Events Column */}
            <div style={{ flex: 1, position: 'relative', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '8px' }}>
              {/* Hour slots */}
              {hours.map(hour => (
                <div 
                  key={hour} 
                  style={{
                    height: '80px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                />
              ))}

              {/* Events */}
              {events.map(event => {
                const style = getEventStyle(event);
                const isAppointment = event.event_type === 'crm_appointment';
                const startTime = fromUTCToSaoPaulo(event.start_date);
                const endTime = event.end_date ? fromUTCToSaoPaulo(event.end_date) : new Date(startTime.getTime() + 30 * 60 * 1000);
                const contact = contactsData[event.contact_id];
                
                return (
                  <div
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    style={{
                      position: 'absolute',
                      left: '8px',
                      right: '8px',
                      top: style.top,
                      height: style.height,
                      backgroundColor: event.color || (isAppointment ? 'var(--primary-color)' : '#44e1ff'),
                      borderRadius: '8px',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      color: '#fff',
                      borderLeft: '4px solid rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                        <AppIcon name={getEventTypeIcon(event.event_type)} style={{ fontSize: '1.1rem' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {event.title}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                        {startTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} - {endTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {contact && (
                        <div style={{ fontSize: '0.8rem', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <AppIcon name="person" style={{ fontSize: '0.9rem' }} />
                          {contact.nome}
                          {contact.numero && <span>• {formatPhoneNumber(contact.numero)}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {events.length === 0 && (
                <div style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  color: '#9bb3c7',
                  textAlign: 'center'
                }}>
                  <AppIcon name="event_busy" style={{ fontSize: '3rem', opacity: 0.5 }} />
                  <p>Nenhum agendamento neste dia</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '1rem', 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'flex-end' 
        }}>
          <button 
            onClick={onNewEvent}
            style={{ 
              padding: '0.5rem 1rem', 
              background: 'var(--primary-color)', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <AppIcon name="add" style={{ fontSize: '1.2rem' }} />
            Novo Agendamento
          </button>
          <button 
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
